import { TFunction } from 'i18next';
import { systemInfoCapabilities } from 'src/js/data/systemInfo';
import { filterUndefinedRows } from 'src/js/pages/statistic/statistic-mapper';
import { StatisticSectionViewModel } from 'src/js/pages/statistic/statistic-view-model';
import { formatUptime } from 'src/js/pages/streaming/stream/stream-helper';
import { toTitleCase } from 'src/js/util/global-util';

import {
  VIDEO_CODEC,
  VIDIN_CONNECTOR,
  VideoEncoderState,
  VideoEncoderViewModel,
  formatOutputResolutionWithoutFrameRate,
} from './video-encoder-models';

export const mapVidEncToStatistics = (vidEnc: VideoEncoderViewModel, t: TFunction): StatisticSectionViewModel[] => {
  // extract human readable UTC
  const regExpExtractParenthesis = /\(([^)]+)\)/;
  const matches = regExpExtractParenthesis.exec(vidEnc.stats?.UTC);
  if (matches) {
    vidEnc.stats.UTCFormatted = matches[1];
  } else {
    vidEnc.stats.UTCFormatted = vidEnc.stats?.UTC;
  }

  const stats = [
    {
      title: t('statistics.header.statistics'),
      rows: [
        {
          name: t('general.status'),
          value: toTitleCase(vidEnc.stateString, false),
        },
        {
          name: t('videnc.statistics.upTime'),
          value: formatUptime(vidEnc.stats.uptime),
        },
        {
          name: t('videnc.statistics.inputPresent'),
          value: vidEnc.stats.inputPresent ? t('general.yes') : t('general.no'),
        },
        {
          name: t('videnc.statistics.inputType'),
          value: vidEnc.stats.inputTypeString,
          when: vidEnc.stats.inputPresent,
        },
        {
          name: t('videnc.statistics.inputFormat'),
          value: vidEnc.stats.inputFormatShort,
          when: vidEnc.stats.inputPresent,
        },
        {
          name: t('videnc.statistics.hdcp'),
          value: vidEnc.hdcp ? t('general.on') : t('general.off'),
          when:
            vidEnc.stats.inputPresent &&
            (vidEnc.inputInterface === VIDIN_CONNECTOR.HDMI || vidEnc.inputInterface === VIDIN_CONNECTOR.DISPLAYPORT),
        },
        {
          name: t('videnc.statistics.hdcpAuthenticated'),
          value: vidEnc.hdcpAuthenticated ? t('general.on') : t('general.off'),
          when:
            vidEnc.stats.inputPresent &&
            (vidEnc.inputInterface === VIDIN_CONNECTOR.HDMI || vidEnc.inputInterface === VIDIN_CONNECTOR.DISPLAYPORT),
        },
        {
          name: t('videnc.statistics.hdcpEncrypted'),
          value: vidEnc.hdcpEncrypted ? t('general.on') : t('general.off'),
          when:
            vidEnc.stats.inputPresent &&
            (vidEnc.inputInterface === VIDIN_CONNECTOR.HDMI || vidEnc.inputInterface === VIDIN_CONNECTOR.DISPLAYPORT),
        },
        {
          name: t('videnc.statistics.aspectRatio'),
          value: vidEnc.stats.aspectRatio,
          when: vidEnc.stats.inputPresent,
        },
        {
          name: t('videnc.statistics.inputColorPrimaries'),
          value: vidEnc.stats.inputColorPrimaries,
          when: !systemInfoCapabilities.is2110() && vidEnc.stats.inputPresent,
        },
        {
          name: t('videnc.statistics.inputTransferCharacteristics'),
          value: vidEnc.stats.inputTransferCharacteristics,
          when: !systemInfoCapabilities.is2110() && vidEnc.stats.inputPresent,
        },
        {
          name: t('videnc.statistics.inputMatrixCoefficients'),
          value: vidEnc.stats.inputMatrixCoefficients,
          when: !systemInfoCapabilities.is2110() && vidEnc.stats.inputPresent,
        },
        {
          name: t('videnc.statistics.signaledDynamicRange'),
          value: vidEnc.stats.signaledDynamicRange,
          when: !systemInfoCapabilities.is2110() && vidEnc.stats.inputPresent,
        },
        {
          name: t('videnc.statistics.resolution'),
          value: formatOutputResolutionWithoutFrameRate(vidEnc),
        },
        {
          name: t('videnc.statistics.encodedFrames'),
          value: vidEnc.stats.encodedFrames,
        },
        {
          name: t('videnc.statistics.encodedBytes'),
          value: vidEnc.stats.encodedBytes,
        },
        {
          name: t('videnc.statistics.encodedFrameRate'),
          value: `${vidEnc.stats.encodedFrameRate} FPS`,
          when: !!vidEnc.stats.encodedFrameRate,
        },
        {
          name: t('videnc.statistics.numberOfSlices'),
          value: vidEnc.stats.numberOfSlices,
          when: vidEnc.stats.state === VideoEncoderState.WORKING,
        },
        {
          name: t('videnc.statistics.droppedFrames'),
          value: vidEnc.stats.droppedFrames,
        },
        {
          name: t('videnc.statistics.encoderResets'),
          value: vidEnc.stats.encoderResets,
        },
        {
          name: t('videnc.statistics.encodedBitRate'),
          value: vidEnc.stats.encodedBitRate,
        },
        {
          name: t('videnc.statistics.encoderPTS'),
          value: vidEnc.stats.encoderPTS,
        },
        {
          name: t('videnc.statistics.encoderLoad'),
          value: vidEnc.stats.encoderLoad,
        },
        {
          name: t('videnc.statistics.scalerLoad'),
          value: vidEnc.stats.scalerLoad !== undefined ? vidEnc.stats.scalerLoad.toString() + '%' : undefined,
        },
        {
          name: t('videnc.statistics.closedCaptioning'),
          value: vidEnc.stats.closedCaptioning ? t('general.enabled') : t('general.disabled'),
        },
        {
          name: t('videnc.statistics.extractedCCBytes'),
          value: vidEnc.stats.extractedCCBytes,
        },
        {
          name: t('videnc.statistics.CCErrors'),
          value: vidEnc.stats.CCErrors,
        },
        {
          name: t('videnc.statistics.extractedCSDBytes'),
          value: vidEnc.stats.extractedCCBytes,
        },
        {
          name: t('videnc.statistics.extractedAFDBytes'),
          value: vidEnc.stats.extractedCCBytes,
        },
        {
          name: t('videnc.statistics.timecodeSource'),
          value: vidEnc.stats.timecodeSource,
        },
        {
          name: t('videnc.statistics.timecode'),
          value: vidEnc.stats.timecode,
        },
        {
          name: t('videnc.statistics.sourceSwitches'),
          value: vidEnc.stats.sourceSwitches,
        },
        {
          name: t('videnc.statistics.UTC'),
          value: vidEnc.stats.UTCFormatted,
        },
        {
          name: t('videnc.statistics.h265Profile'),
          value: vidEnc.stats.h265Profile,
          when: vidEnc.codecAlgorithm === VIDEO_CODEC.H265 && vidEnc.stats.state === VideoEncoderState.WORKING,
        },
        {
          name: t('videnc.statistics.h265Tier'),
          value: vidEnc.stats.h265Tier,
          when: vidEnc.codecAlgorithm === VIDEO_CODEC.H265 && vidEnc.stats.state === VideoEncoderState.WORKING,
        },
        {
          name: t('videnc.statistics.h265Level'),
          value: vidEnc.stats.h265Level,
          when: vidEnc.codecAlgorithm === VIDEO_CODEC.H265 && vidEnc.stats.state === VideoEncoderState.WORKING,
        },
        {
          name: t('videnc.statistics.h264Profile'),
          value: vidEnc.stats.h264Profile,
          when: vidEnc.codecAlgorithm === VIDEO_CODEC.H264 && vidEnc.stats.state === VideoEncoderState.WORKING,
        },
        {
          name: t('videnc.statistics.h264Level'),
          value: vidEnc.stats.h264Level,
          when: vidEnc.codecAlgorithm === VIDEO_CODEC.H264 && vidEnc.stats.state === VideoEncoderState.WORKING,
        },
        {
          name: t('videnc.statistics.sceneChangesDetected'),
          value: vidEnc.stats.sceneChangesDetected,
        },
        {
          name: t('videnc.statistics.lastSceneChange'),
          value: vidEnc.stats.lastSceneChange,
        },
        {
          name: t('statistics.stream.rows.lastReset'),
          value: formatUptime(vidEnc.stats.lastReset),
        },
      ],
    },
  ];

  return filterUndefinedRows(stats);
};
